'use client';
import React, { useEffect, useState } from 'react';
import Bugsnag from '@bugsnag/js';
import '@/app/globals.css';
import { looksLikeCacheError, parseVersionText, STALE_CACHE_GROUPING_HASH } from '@/app/lib/staleClient';

// Version of the bundle this tab is running (baked at build time).
const RUNNING_UI_VERSION = process.env.APP_VERSION || 'dev';

type Props = {
  error?: Error & { digest?: string };
  reset?: () => void;
};

/**
 * Report an error to Bugsnag from a Next.js App Router error boundary.
 * Route-level errors bypass the React error boundary in `BugsnagBoundary`,
 * so `error.tsx` and `global-error.tsx` must notify manually.
 */
export function notifyBugsnag(
  error: Error & { digest?: string } | undefined,
  opts: {
    source: string;
    severity?: 'info' | 'warning' | 'error';
    extra?: Record<string, unknown>;
    groupingHash?: string; // override Bugsnag grouping (e.g. collapse per-deploy chunk hashes)
  } = { source: 'unknown' },
) {
  if (!error) return;
  try {
    Bugsnag.notify(error, (event) => {
      event.severity = opts.severity ?? 'error';
      if (opts.groupingHash) event.groupingHash = opts.groupingHash;
      if (error.digest) {
        event.addMetadata('nextjs', { digest: error.digest });
      }
      event.addMetadata('error_boundary', {
        source: opts.source,
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        ...opts.extra,
      });
    });
  } catch {
    // Bugsnag not started yet — fall back to console so it's still visible in dev
    // eslint-disable-next-line no-console
    console.error(`[${opts.source}] uncaught error (Bugsnag not started):`, error);
  }
}

const CACHE_RETRY_KEY = 'powston_cache_retry';

export const FallbackComponent = ({ error }: { error?: Error }) => {
  return (<ErrorComponent error={error} />);
};

export const ErrorComponent = ({ error, reset }: { error?: Error & { digest?: string }; reset?: () => void }) => {
  const [attempted, setAttempted] = useState(false);
  const [deployedUiVersion, setDeployedUiVersion] = useState<string | null>(null);
  const [apiVersion, setApiVersion] = useState<string | null>(null);
  const isCacheIssue = looksLikeCacheError(error);

  // Fetch the deployed UI version and API version so the error screen (and
  // anyone screenshotting it) shows whether this tab is running an old
  // bundle. Best effort — the error page must render even if these fail.
  useEffect(() => {
    fetch('/version.txt', { cache: 'no-store' })
      .then(res => (res.ok ? res.text() : null))
      .then(text => {
        const v = parseVersionText(text);
        if (v) setDeployedUiVersion(v);
      })
      .catch(() => {});
    fetch('/api/health_checker')
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (data?.version) setApiVersion(String(data.version));
      })
      .catch(() => {});
  }, []);

  const uiOutdated = !!deployedUiVersion && deployedUiVersion !== RUNNING_UI_VERSION;

  // Report to Bugsnag once per error. Cache-related errors are reported too
  // (useful to see how often stale chunks hit users) but at lower severity.
  useEffect(() => {
    notifyBugsnag(error, {
      source: 'app/error.tsx',
      severity: isCacheIssue ? 'warning' : 'error',
      // Chunk filenames are content-hashed per deploy; group all stale-cache
      // errors under one stable hash so each deploy doesn't mint a new
      // Bugsnag error group that has to be re-ignored.
      groupingHash: isCacheIssue ? STALE_CACHE_GROUPING_HASH : undefined,
      extra: { cacheIssue: isCacheIssue },
    });
  }, [error, isCacheIssue]);

  // Auto-retry once for cache errors: clear caches and hard reload
  useEffect(() => {
    if (!isCacheIssue) return;

    const retryCount = parseInt(sessionStorage.getItem(CACHE_RETRY_KEY) || '0', 10);
    if (retryCount >= 1) {
      // Already tried once, don't loop
      sessionStorage.removeItem(CACHE_RETRY_KEY);
      return;
    }

    sessionStorage.setItem(CACHE_RETRY_KEY, String(retryCount + 1));
    setAttempted(true);

    // Clear service worker caches if available
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }

    // Hard reload after a brief moment
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }, [isCacheIssue]);

  if (attempted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-gray-300">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-sm">Clearing stale cache and reloading...</p>
        </div>
      </div>
    );
  }

  const handleFreshReload = () => {
    // Bust all caches and force reload
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    // Add cache-bust param to force fresh fetch
    const url = new URL(window.location.href);
    url.searchParams.set('_cb', Date.now().toString());
    window.location.href = url.toString();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-gray-100">
      <div className="w-full max-w-2xl mx-4 p-8 rounded-lg bg-zinc-900 shadow-2xl border border-zinc-800">
        <div className="flex flex-col items-center text-center">

          {/* Powston + Black Knight mashup */}
          <div className="mb-2">
            <span className="text-xs font-mono tracking-widest text-amber-500/70 uppercase">Powston</span>
          </div>

          <div className="relative">
            {/* Crossed swords emoji as the knight icon */}
            <div className="text-6xl mb-2 select-none" aria-hidden>&#9876;&#65039;</div>
            <h1 className="text-3xl font-extrabold tracking-tight text-gray-100">
              &ldquo;Tis but a scratch!&rdquo;
            </h1>
          </div>

          <p className="mt-3 text-sm text-gray-400 max-w-md leading-relaxed">
            The Black Knight has encountered a setback. The page didn&apos;t load,
            but like any good knight, it refuses to admit defeat.
          </p>

          {/* What went wrong hint */}
          {(isCacheIssue || uiOutdated) && (
            <div className="mt-4 w-full rounded-lg bg-amber-900/20 border border-amber-700/40 px-4 py-3 text-left">
              <p className="text-sm text-amber-300 font-medium">Looks like a stale cache</p>
              <p className="text-xs text-amber-400/70 mt-1">
                {uiOutdated
                  ? `Your browser is running version ${RUNNING_UI_VERSION}, but ${deployedUiVersion} has been released. A fresh reload should sort it out.`
                  : 'A recent update may have left your browser holding onto old page data. A fresh reload should sort it out.'}
              </p>
            </div>
          )}

          {error?.message && !isCacheIssue && (
            <div className="mt-4 w-full rounded-lg bg-zinc-800/50 border border-zinc-700/40 px-4 py-3 text-left">
              <p className="text-xs font-mono text-gray-500 break-all">{error.message}</p>
            </div>
          )}

          {/* Health bar */}
          <div className="mt-6 w-full">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span>System integrity</span>
              <span>The Black Knight always triumphs</span>
            </div>
            <div className="h-2.5 w-full bg-zinc-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-red-700 to-red-500 animate-pulse rounded-full"
                style={{ width: '14%' }}
                aria-hidden
              />
            </div>
          </div>

          {/* Limb report - compact */}
          <div className="mt-5 grid grid-cols-4 gap-2 w-full text-center">
            {[
              { limb: 'Left Arm', icon: '💪', status: 'Severed' },
              { limb: 'Right Arm', icon: '🦾', status: 'Had worse' },
              { limb: 'Left Leg', icon: '🦵', status: 'A scratch' },
              { limb: 'Right Leg', icon: '🦿', status: 'Still kicking' },
            ].map(x => (
              <div key={x.limb} className="rounded-lg border border-zinc-800 p-2 bg-zinc-950/50">
                <div className="text-lg" aria-hidden>{x.icon}</div>
                <p className="text-[10px] font-medium text-gray-400 mt-0.5">{x.limb}</p>
                <p className="text-[10px] text-gray-600">{x.status}</p>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            {(isCacheIssue || uiOutdated) ? (
              <button
                onClick={handleFreshReload}
                className="px-5 py-2.5 rounded-lg bg-amber-600 text-white font-semibold hover:bg-amber-500 transition-colors"
              >
                Reload Fresh Page
              </button>
            ) : reset ? (
              <button
                onClick={reset}
                className="px-5 py-2.5 rounded-lg bg-amber-600 text-white font-semibold hover:bg-amber-500 transition-colors"
              >
                Try Again
              </button>
            ) : (
              <button
                onClick={() => window.location.reload()}
                className="px-5 py-2.5 rounded-lg bg-amber-600 text-white font-semibold hover:bg-amber-500 transition-colors"
              >
                Try Again
              </button>
            )}
            <a
              href="/"
              className="px-5 py-2.5 rounded-lg bg-zinc-800 text-gray-100 font-semibold hover:bg-zinc-700 transition-colors"
            >
              Run Away!
            </a>
            <a
              href="/system-status"
              className="px-5 py-2.5 rounded-lg bg-zinc-800 text-gray-100 font-semibold hover:bg-zinc-700 transition-colors"
            >
              System Status
            </a>
          </div>

          <p className="mt-6 text-xs text-gray-600 italic">
            &ldquo;Come back here and take what&apos;s coming to you! I&apos;ll bite your legs off!&rdquo;
            <span className="block mt-1 not-italic text-gray-700">— The Black Knight, Monty Python and the Holy Grail</span>
          </p>

          {/* Version line so support can tell from a screenshot whether this
              tab was running an outdated bundle when it crashed. */}
          <p className="mt-3 text-[10px] font-mono text-gray-600">
            ui:{RUNNING_UI_VERSION}
            {uiOutdated && <span className="text-amber-500"> (deployed: {deployedUiVersion})</span>}
            {apiVersion && <span> · api:{apiVersion}</span>}
          </p>
        </div>
      </div>
    </div>
  );
};

export default function BlackKnightError({ error, reset }: Props) {
  return <ErrorComponent error={error} reset={reset} />;
}
